import * as Sentry from '@sentry/browser';
import idx from 'idx';

import { EventTrackingAction, UserEvent } from '../declarations';
import { ContextualData, emptyContextualData } from '../contextualData';
import { DeployEnv, getDeployEnv } from './deployEnv';

export { Sentry };

export type SentryOptions = {
  dsn: string;
  appName: string;
} & Omit<Omit<Sentry.BrowserOptions, 'environment'>, 'release'>;

export function initSentry(
  env: DeployEnv,
  gitSha: string,
  { ignoreErrors: origIgnoreErrors = [], appName, ...restOfSentryOpts }: SentryOptions,
): void {
  // don't install Sentry in server side rendering
  if (typeof window === 'undefined') {
    return;
  }

  // don't install Sentry in development mode
  if (env === 'development') {
    return;
  }

  Sentry.init({
    environment: env,
    release: `${appName}@${gitSha}`,
    ignoreErrors: [...DEFAULT_IGNORE_ERRORS, ...origIgnoreErrors],
    beforeSend,
    ...restOfSentryOpts,
  });
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    Sentry.captureException(event.reason);
  });
}

export function trackEventInSentry(
  action: EventTrackingAction,
  label?: string,
  contextualData: ContextualData = emptyContextualData,
) {
  // TODO: Map actions to different breadcrumb types? https://develop.sentry.dev/sdk/event-payloads/breadcrumbs/#breadcrumb-types
  Sentry.addBreadcrumb({
    category: action,
    message: label,
    data: {
      ...contextualData.data,
      adClickContext: contextualData.adClickContext,
      campaignContext: contextualData.campaignContext,
      customerContext: contextualData.customerContext,
      leadContext: contextualData.leadContext,
      sellDirectContext: contextualData.sellDirectContext,
    },
    timestamp: Date.now(),
  });
}

export function trackUserEventInSentry(
  event: UserEvent,
  contextualData: ContextualData = emptyContextualData,
) {
  Sentry.addBreadcrumb({
    category: Object.keys(event)[0] ?? 'unknownUserEvent',
    data: {
      ...event,
      ...contextualData.data,
      adClickContext: contextualData.adClickContext,
      campaignContext: contextualData.campaignContext,
      customerContext: contextualData.customerContext,
      leadContext: contextualData.leadContext,
      sellDirectContext: contextualData.sellDirectContext,
    },
    timestamp: Date.now(),
  });
}

export function trackErrorInSentry(
  label: string,
  error?: Error,
  contextualData: ContextualData = emptyContextualData,
): ContextualData {
  let newContextualData: ContextualData = emptyContextualData;
  Sentry.withScope((scope) => {
    scope.setFingerprint([label]);
    scope.setUser(
      contextualData.user
        ? {
            ...contextualData.user,
            id: contextualData.user.humanID,
          }
        : null,
    );

    scope.setTags({
      ...contextualDataToTags(contextualData.data),
      label, // add label as a tag so we can search for it
    });
    scope.setExtras(contextualData.data);

    const eventId = Sentry.captureException(error || new Error(label));
    newContextualData = {
      data: {
        'sentry-event-id': eventId,
      },
    };
  });
  return newContextualData;
}

/**
 * Sentry tags are powerful because they are indexed and can be searched in the Sentry app.
 * However, there are restrictions, as documented here: https://docs.sentry.io/platforms/javascript/enriching-events/tags/
 * This function converts all simple (i.e. non-object) data values into tags.
 */
function contextualDataToTags(data: { [k: string]: unknown }): { [k: string]: string } {
  let tags: { [k: string]: string } = {};
  Object.keys(data).forEach((key) => {
    const value = data[key];
    switch (typeof value) {
      case 'undefined':
      case 'boolean':
      case 'number':
      case 'bigint':
      case 'string':
      case 'symbol':
        tags[key] = String(value);
        break;
      case 'object':
        if (value === null) {
          tags[key] = 'null';
        }
        break;
    }
  });
  return tags;
}

// Also used in js/packages/cosmos/src/reporters/sentry.ts
// Consider keeping the two in sync.
// Please add a comment for each ignored error.
const DEFAULT_IGNORE_ERRORS: ReadonlyArray<string | RegExp> = [
  // Error generated by a bug in auto-fill library from Chrome Mobile on iOS
  // https://github.com/getsentry/sentry/issues/5267
  'Blocked a frame with origin',

  // error generated by microsoft outlook's safelink crawler on pages we email out
  // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062
  'Object Not Found Matching Id',

  // Error that MAY indicate a performance issue due to browser implementation, but has been shown to be safe to ignore.
  // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
];

type Mapper = (arg: Sentry.Event | null) => Sentry.Event | null;

const beforeSendFn = (mappers: ReadonlyArray<Mapper>, event: Sentry.Event) => {
  return mappers.reduce<Sentry.Event | null>((acc, mapper) => mapper(acc), event);
};

const beforeSend = (event: Sentry.Event | null): Sentry.Event | null => {
  if (!event) {
    return null;
  }

  return beforeSendFn(MAPPERS, event);
};

const isPhantomJs: Mapper = (event) => {
  // Silences sentries for crawlers/prerender.io
  const isPhantomJS = /PhantomJS/.test(window.navigator.userAgent);
  return !isPhantomJS ? event : null;
};

// ignored because of this stackoverflow https://stackoverflow.com/questions/51744083/invalid-argument-google-map-error-in-edge-browser
const isInvalidArgumentError: Mapper = (event) => {
  const firstFrameFilename =
    idx(event, (_) => _.exception.values[0].stacktrace.frames[0].filename) || '';
  const eventMessage = idx(event, (_) => _.exception.values[0].value) || '';

  if (eventMessage.includes('Invalid argument') && firstFrameFilename.includes('maps-api-v3')) {
    return null;
  }
  if (
    eventMessage.includes('Could not load "map"') ||
    eventMessage.includes('Could not load "marker"') ||
    // Error occurs when watching Vimeo videos on Safari
    eventMessage.includes('The operation was aborted.')
  ) {
    return null;
  }
  return event;
};

const MAPPERS: ReadonlyArray<Mapper> = [isPhantomJs, isInvalidArgumentError];

export const test = { beforeSend, beforeSendFn, isInvalidArgumentError, isPhantomJs };

/**
 * TODO:
 * This is kept for backwards compatibility. We should let observability lib handle Sentry initialization,
 * which might require us to build a version of observability for non-consumer apps.
 */
export const loadSentry = (
  url: string,
  sentryOptions: Omit<SentryOptions, 'dsn' | 'appName'> = {},
) => {
  const env = getDeployEnv();
  const gitSha = process.env.REACT_APP_GIT_SHA || 'unknown';
  initSentry(env, gitSha, {
    dsn: url,
    appName: '',
    ...sentryOptions,
  });
};
